import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Camera, Upload, User, Users, Ship, FileText, CheckCircle } from "lucide-react";
import SEOHead, { membershipPageSEO } from "@/components/SEOHead";

const applicationFormSchema = z.object({
  // Personal Information
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  identityNumber: z.string().min(1, "Identity number is required"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  
  // Partner Information
  partnerName: z.string().optional(),
  partnerDateOfBirth: z.string().optional(),
  
  // Children Information
  childrenNames: z.array(z.string()).optional(),
  childrenDatesOfBirth: z.array(z.string()).optional(),
  
  // Contact Information
  postalAddress: z.string().min(1, "Postal address is required"),
  postalCode: z.string().min(1, "Postal code is required"),
  homeAddress: z.string().optional(),
  homeCode: z.string().optional(),
  workPhone: z.string().optional(),
  homePhone: z.string().optional(),
  mobilePhone: z.string().min(1, "Mobile phone is required"),
  email: z.string().email("Valid email is required"),
  
  // Emergency Contact
  emergencyContact: z.string().min(1, "Emergency contact is required"),
  emergencyAlternativeName: z.string().optional(),
  emergencyContactNumber: z.string().min(1, "Emergency contact number is required"),
  
  // Professional Information
  occupation: z.string().optional(),
  typeOfBusiness: z.string().optional(),
  employer: z.string().optional(),
  otherClubs: z.string().optional(),
  activities: z.string().optional(),
  
  // Boating Experience
  boatingExperience: z.string().optional(),
  
  // Craft Information
  craftType1: z.string().optional(),
  craftName1: z.string().optional(),
  craftRegNo1: z.string().optional(),
  craftType2: z.string().optional(),
  craftName2: z.string().optional(),
  craftRegNo2: z.string().optional(),
  ownership: z.string().optional(),
  
  // Membership Details
  membershipCategory: z.string().min(1, "Membership category is required"),
  temporaryFromDate: z.string().optional(),
  temporaryToDate: z.string().optional(),
  skippersName: z.string().optional(),
  
  // Proposers
  proposedBy1Name: z.string().min(1, "First proposer name is required"),
  proposedBy2Name: z.string().min(1, "Second proposer name is required"),
});

type ApplicationFormData = z.infer<typeof applicationFormSchema>;

export default function Apply() {
  const [currentStep, setCurrentStep] = useState(1);
  const [photoFile, setPhotoFile] = useState<File | undefined>(undefined);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [children, setChildren] = useState<Array<{ name: string; dateOfBirth: string }>>([]);
  const { toast } = useToast();

  const form = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: {
      membershipCategory: "",
      childrenNames: [],
      childrenDatesOfBirth: [],
    },
  });

  const submitApplication = useMutation({
    mutationFn: async (data: ApplicationFormData & { photoFile?: File }) => {
      const formData = new FormData();
      
      // Add all form fields
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'photoFile' && value !== undefined) {
          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });
      
      // Add photo file if present
      if (photoFile) {
        formData.append('photo', photoFile);
      }

      const response = await fetch("/api/membership-applications", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to submit application");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Application Submitted Successfully!",
        description: "Your membership application has been received and will be reviewed by the committee.",
      });
      setCurrentStep(5); // Success step
    },
    onError: () => {
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your application. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const addChild = () => {
    setChildren([...children, { name: "", dateOfBirth: "" }]);
  };

  const removeChild = (index: number) => {
    const newChildren = children.filter((_, i) => i !== index);
    setChildren(newChildren);
    form.setValue("childrenNames", newChildren.map(child => child.name));
    form.setValue("childrenDatesOfBirth", newChildren.map(child => child.dateOfBirth));
  };

  const updateChild = (index: number, field: "name" | "dateOfBirth", value: string) => {
    const newChildren = [...children];
    newChildren[index][field] = value;
    setChildren(newChildren);
    form.setValue("childrenNames", newChildren.map(child => child.name));
    form.setValue("childrenDatesOfBirth", newChildren.map(child => child.dateOfBirth));
  };

  const onSubmit = (data: ApplicationFormData) => {
    submitApplication.mutate(data);
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = [
    { number: 1, title: "Personal Info", icon: User },
    { number: 2, title: "Contact & Emergency", icon: Users },
    { number: 3, title: "Boating & Membership", icon: Ship },
    { number: 4, title: "Photo & Proposers", icon: Camera },
  ];

  if (currentStep === 5) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="p-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h1>
            <p className="text-gray-600 mb-6">
              Thank you for applying to join the Vaal Cruising Association. Your application will be reviewed by our committee and you will be contacted within 8 weeks.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-blue-800">
                <strong>Next Steps:</strong><br />
                • Committee review (8 weeks)<br />
                • Banking details will be provided upon acceptance<br />
                • Welcome email with further instructions
              </p>
            </div>
            <Button 
              onClick={() => window.location.href = "/"} 
              className="bg-orange-500 hover:bg-orange-600"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEOHead {...membershipPageSEO} />
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <FileText className="h-12 w-12 mx-auto mb-4 text-blue-200" />
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Membership Application</h1>
          <p className="text-blue-100">Join the Vaal Cruising Association Community</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            {steps.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <div key={step.number} className="flex flex-col items-center flex-1">
                  <div className={`
                    w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center mb-2
                    ${isActive ? 'bg-orange-500 text-white' : 
                      isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}
                  `}>
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </div>
                  <span className={`text-xs sm:text-sm font-medium text-center
                    ${isActive ? 'text-orange-600' : 
                      isCompleted ? 'text-green-600' : 'text-gray-500'}
                  `}>
                    {step.title}
                  </span>
                  {step.number < steps.length && (
                    <div className={`hidden sm:block w-full h-1 mt-2
                      ${isCompleted ? 'bg-green-300' : 'bg-gray-200'}
                    `} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Personal Information
                  </CardTitle>
                  <CardDescription>
                    Please provide your personal details and family information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter first name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter last name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="identityNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Identity Number *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter ID number" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth *</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Partner Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="partnerName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Partner Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter partner name (optional)" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="partnerDateOfBirth"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Partner Date of Birth</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">Children Information</h3>
                      <Button type="button" onClick={addChild} variant="outline" size="sm">
                        Add Child
                      </Button>
                    </div>
                    {children.map((child, index) => (
                      <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 p-4 border rounded-lg">
                        <div>
                          <label className="text-sm font-medium">Child's Name</label>
                          <Input
                            placeholder="Enter child's name"
                            value={child.name}
                            onChange={(e) => updateChild(index, "name", e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Date of Birth</label>
                          <div className="flex gap-2">
                            <Input
                              type="date"
                              value={child.dateOfBirth}
                              onChange={(e) => updateChild(index, "dateOfBirth", e.target.value)}
                            />
                            <Button
                              type="button"
                              onClick={() => removeChild(index)}
                              variant="outline"
                              size="sm"
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Contact & Emergency Information */}
            {currentStep === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Contact & Emergency Information
                  </CardTitle>
                  <CardDescription>
                    Your contact details and emergency contact information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="postalAddress"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Postal Address *</FormLabel>
                              <FormControl>
                                <Textarea placeholder="Enter postal address" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="postalCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Postal Code *</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter postal code" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="homeAddress"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Home Address</FormLabel>
                              <FormControl>
                                <Textarea placeholder="Enter home address (optional)" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="homeCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Home Code</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter home code (optional)" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="workPhone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Work Phone</FormLabel>
                              <FormControl>
                                <Input placeholder="Work phone (optional)" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="homePhone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Home Phone</FormLabel>
                              <FormControl>
                                <Input placeholder="Home phone (optional)" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="mobilePhone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Mobile Phone *</FormLabel>
                              <FormControl>
                                <Input placeholder="Mobile phone" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address *</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="Enter email address" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Emergency Contact</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="emergencyContact"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Emergency Contact Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter emergency contact name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="emergencyContactNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Emergency Contact Number *</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter emergency contact number" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="emergencyAlternativeName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Alternative Emergency Contact</FormLabel>
                            <FormControl>
                              <Input placeholder="Alternative emergency contact (optional)" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Professional Information</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="occupation"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Occupation</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter occupation (optional)" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="typeOfBusiness"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Type of Business</FormLabel>
                              <FormControl>
                                <Input placeholder="Type of business (optional)" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="employer"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Employer</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter employer (optional)" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="otherClubs"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Other Clubs</FormLabel>
                            <FormControl>
                              <Input placeholder="Other club memberships (optional)" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="activities"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Activities</FormLabel>
                            <FormControl>
                              <Textarea placeholder="Activities and interests (optional)" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Boating & Membership */}
            {currentStep === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Ship className="h-5 w-5" />
                    Boating Experience & Membership
                  </CardTitle>
                  <CardDescription>
                    Tell us about your boating experience and choose your membership type
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Boating Experience</h3>
                    <FormField
                      control={form.control}
                      name="boatingExperience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Yachting, Boating and Club Admin Experience</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Describe your boating experience, qualifications, and any club administration experience..."
                              rows={4}
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Craft Information</h3>
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-3">Craft 1</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <FormField
                            control={form.control}
                            name="craftType1"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Type</FormLabel>
                                <FormControl>
                                  <Input placeholder="e.g., Yacht, Powerboat" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="craftName1"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Craft name" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="craftRegNo1"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Registration Number</FormLabel>
                                <FormControl>
                                  <Input placeholder="Registration number" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium mb-3">Craft 2 (Optional)</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <FormField
                            control={form.control}
                            name="craftType2"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Type</FormLabel>
                                <FormControl>
                                  <Input placeholder="e.g., Yacht, Powerboat" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="craftName2"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Craft name" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="craftRegNo2"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Registration Number</FormLabel>
                                <FormControl>
                                  <Input placeholder="Registration number" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="ownership"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Ownership</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select ownership type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Owner">Owner</SelectItem>
                                <SelectItem value="Part Owner">Part Owner</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Membership Category</h3>
                    <FormField
                      control={form.control}
                      name="membershipCategory"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Select Membership Type *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Choose membership category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Ordinary Member">Ordinary Member (Member + partner & children under 12)</SelectItem>
                              <SelectItem value="Social Member">Social Member (Limited to 48 days per year)</SelectItem>
                              <SelectItem value="Intermediate Member">Intermediate Member (Single person 19-25 years)</SelectItem>
                              <SelectItem value="Junior Member">Junior Member (Birth to 18 years)</SelectItem>
                              <SelectItem value="Crew Visitor">Crew Visitor</SelectItem>
                              <SelectItem value="Temporary Member">Temporary Member</SelectItem>
                              <SelectItem value="Associate Membership">Associate Membership</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {form.watch("membershipCategory") === "Temporary Member" && (
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="temporaryFromDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>From Date</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="temporaryToDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>To Date</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    {form.watch("membershipCategory") === "Crew Visitor" && (
                      <div className="mt-4">
                        <FormField
                          control={form.control}
                          name="skippersName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Skipper's Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter skipper's name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Photo & Proposers */}
            {currentStep === 4 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="h-5 w-5" />
                    Photo & Proposers
                  </CardTitle>
                  <CardDescription>
                    Upload your photo and provide proposer information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Applicant Photo</h3>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      {photoPreview ? (
                        <div className="space-y-4">
                          <img 
                            src={photoPreview} 
                            alt="Preview" 
                            className="w-32 h-32 object-cover rounded-lg mx-auto"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setPhotoPreview(null);
                              setPhotoFile(undefined);
                            }}
                          >
                            Remove Photo
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                          <div>
                            <label className="cursor-pointer">
                              <span className="text-blue-600 hover:text-blue-500">
                                Click to upload a photo
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                className="hidden"
                              />
                            </label>
                            <p className="text-gray-500 text-sm mt-2">
                              JPG, PNG up to 5MB. Photo is required for membership application.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Proposers</h3>
                    <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4">
                      <p className="text-amber-800 text-sm">
                        <strong>Important:</strong> Your application must be proposed by two existing VCA members in good standing. 
                        Please ensure you have spoken to your proposers before submitting this application.
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium mb-3">First Proposer</h4>
                        <FormField
                          control={form.control}
                          name="proposedBy1Name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Proposer Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter first proposer's full name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div>
                        <h4 className="font-medium mb-3">Second Proposer</h4>
                        <FormField
                          control={form.control}
                          name="proposedBy2Name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Proposer Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter second proposer's full name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Application Agreement</h4>
                    <p className="text-blue-800 text-sm">
                      By submitting this application, I hereby agree, on acceptance of my membership to the Association, 
                      to abide by the Constitution and Rules of the Association. This includes the Social Media Policy 
                      as well as the WhatsApp Rules. I acknowledge that VCA abides by the POPI Act as prescribed.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6">
              {currentStep > 1 && (
                <Button type="button" onClick={prevStep} variant="outline">
                  Previous
                </Button>
              )}
              
              <div className="ml-auto flex gap-2">
                {currentStep < 4 ? (
                  <Button type="button" onClick={nextStep} className="bg-orange-500 hover:bg-orange-600">
                    Next
                  </Button>
                ) : (
                  <Button 
                    type="submit" 
                    disabled={submitApplication.isPending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {submitApplication.isPending ? "Submitting..." : "Submit Application"}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
    </>
  );
}